import { Link, Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import {
  ChevronsUpDown,
  LayoutGrid,
  LogOut,
  Monitor,
  Moon,
  Plus,
  Search,
  Sun,
  WifiOff,
} from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { Popover } from 'radix-ui';
import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
  type ComponentType,
  lazy,
  Suspense,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useLiveRefresh } from '@/app/useLiveRefresh';
import { flushHarvestQueue, pendingHarvestDays } from '@/lib/offlineQueue';
import { useAuth } from '@/auth/AuthProvider';
import { useFarmSettings } from '@/features/data';
import { Button } from '@farmos/ui/button';
import { Dialog } from '@farmos/ui/dialog';
import { Segmented } from '@farmos/ui/segmented';
import { requestQuick } from '@/lib/quick';
import { setTheme, type ThemePref, useTheme } from '@/lib/theme';
import { cn } from '@farmos/ui/utils';
import { BrandMark } from './BrandMark';
import { PALETTE_EVENT } from '@/lib/palette';
import { LanguageSwitcher } from './LanguageSwitcher';
import { canSeeToday, NAV, type NavItem, QUICK, type QuickItem } from './nav';

// Loaded after the shell: the bell's alerts and the search palette aren't needed for the first paint.
const NotificationBell = lazy(() =>
  import('@/features/home/NotificationBell').then((m) => ({ default: m.NotificationBell })),
);
const CommandPalette = lazy(() =>
  import('./CommandPalette').then((m) => ({ default: m.CommandPalette })),
);

function useOnline() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [online, setOnline] = useState(() => navigator.onLine);
  // Send harvest days saved while offline as soon as we're back (and at startup).
  useEffect(() => {
    if (!online) return;
    void flushHarvestQueue().then((n) => {
      if (!n) return;
      toast.success(t('harvest.synced', { n }));
      void qc.invalidateQueries();
    });
  }, [online, qc, t]);
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);
  return online;
}

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);

/** Where each screen's <PageHeader> puts its title: the shell's top bar. */
const TitleSlot = createContext<HTMLElement | null>(null);

export function AppShell() {
  const { t } = useTranslation();
  const { perms, profile } = useAuth();
  const navigate = useNavigate();
  const path = useRouterState({ select: (s) => s.location.pathname });
  const online = useOnline();
  useLiveRefresh();
  const [moreOpen, setMoreOpen] = useState(false);
  const [quickOpen, setQuickOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  useEffect(() => {
    const open = () => setPaletteOpen(true);
    window.addEventListener(PALETTE_EVENT, open);
    return () => window.removeEventListener(PALETTE_EVENT, open);
  }, []);
  const [titleEl, setTitleEl] = useState<HTMLElement | null>(null);

  const items = NAV.filter((n) => perms[n.perm]);
  const railItems = items.filter((n) => n.perm !== 'settings');
  const settingsItem = items.find((n) => n.perm === 'settings');
  const quick = QUICK.filter((q) => perms[q.perm]);
  // Workers who can only record the harvest already have it as a tab.
  const showQuick = quick.length > 1;
  const tabs = items.length > 4 ? items.slice(0, 3) : items;
  const overflow = items.length > 4 ? items.slice(3) : [];
  const isActive = (to: string) => path === to || path.startsWith(`${to}/`);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const runQuick = (q: QuickItem) => {
    setQuickOpen(false);
    void navigate({ to: q.to });
    if (q.intent) requestQuick(q.intent);
  };

  return (
    <TitleSlot.Provider value={titleEl}>
      <div className="min-h-dvh lg:ps-28 print:ps-0">
        <a
          href="#main"
          className="sr-only focus:not-sr-only focus:absolute focus:start-2 focus:top-2 focus:z-50 focus:rounded-full focus:bg-panel focus:px-4 focus:py-2"
        >
          Skip to content
        </a>

        <aside className="fixed inset-y-0 start-0 z-30 hidden w-28 flex-col items-center gap-4 py-7 lg:flex print:hidden">
          <Link
            to="/"
            aria-label={t('app.name')}
            className="grid size-16 shrink-0 place-items-center rounded-[20px] bg-panel shadow-card ring-1 ring-line/60 transition-transform duration-200 hover:scale-[1.04]"
          >
            <BrandMark className="size-9" />
          </Link>
          <nav aria-label={t('nav.menu')}>
            <RailGroup>
              {railItems.map((n) => (
                <RailLink key={n.to} item={n} label={t(n.label)} active={isActive(n.to)} />
              ))}
            </RailGroup>
          </nav>
          <div className="flex-1" />
          {settingsItem && (
            <RailGroup>
              <RailLink
                item={settingsItem}
                label={t(settingsItem.label)}
                active={isActive(settingsItem.to)}
              />
            </RailGroup>
          )}
        </aside>

        <header className="sticky top-0 z-30 border-b border-line/60 bg-bg/80 backdrop-blur-xl backdrop-saturate-150 lg:static lg:border-0 lg:bg-transparent lg:backdrop-filter-none print:static print:border-0">
          <div className="mx-auto flex max-w-[88rem] items-center gap-3 px-4 pb-2.5 pt-[max(0.625rem,env(safe-area-inset-top))] sm:px-6 lg:px-10 lg:pb-0 lg:pt-8">
            <Link
              to="/"
              aria-label={t('app.name')}
              className="grid size-11 shrink-0 place-items-center rounded-2xl bg-panel shadow-card ring-1 ring-line/60 lg:hidden"
            >
              <BrandMark className="size-7" />
            </Link>
            <div
              ref={setTitleEl}
              className="flex min-h-11 min-w-0 flex-1 flex-col justify-center lg:min-h-[3.75rem]"
            />
            <div className="flex shrink-0 items-center gap-2 print:hidden">
              <button
                onClick={() => setPaletteOpen(true)}
                aria-label={t('nav.search')}
                className="grid size-11 place-items-center rounded-full text-muted transition-colors hover:bg-soft hover:text-ink lg:hidden"
              >
                <Search className="size-5" aria-hidden />
              </button>
              <button
                onClick={() => setPaletteOpen(true)}
                className="hidden h-12 w-64 items-center gap-2.5 rounded-full border border-white/80 bg-panel/80 px-4 text-sm text-muted shadow-card transition-colors hover:text-ink lg:flex dark:border-line"
              >
                <Search className="size-[18px] shrink-0" aria-hidden />
                <span className="flex-1 truncate text-start">{t('nav.search')}</span>
                <kbd
                  dir="ltr"
                  className="rounded-md border border-line px-1.5 text-[11px] font-medium"
                >
                  {isMac ? '⌘K' : 'Ctrl K'}
                </kbd>
              </button>
              {showQuick && <QuickMenu items={quick} onPick={runQuick} />}
              {canSeeToday(perms) && (
                <Suspense fallback={<span className="size-11 lg:size-12" aria-hidden />}>
                  <NotificationBell />
                </Suspense>
              )}
              <UserMenu />
              <button
                onClick={() => setMoreOpen(true)}
                aria-label={profile?.full_name ?? t('nav.menu')}
                className="rounded-full lg:hidden"
              >
                <Avatar name={profile?.full_name} />
              </button>
            </div>
          </div>
        </header>

        {!online && (
          <div
            role="status"
            className="sticky top-[4.25rem] z-20 mx-auto mt-3 flex w-fit items-center gap-2 rounded-full bg-ng px-4 py-1.5 text-sm font-medium text-white shadow-pop lg:top-4"
          >
            <WifiOff className="size-4" aria-hidden /> {t('common.offline')}
            {pendingHarvestDays().length > 0 && (
              <span className="rounded-full bg-white/20 px-2 py-0.5 text-xs">
                {t('common.offline_waiting', { count: pendingHarvestDays().length })}
              </span>
            )}
          </div>
        )}

        <main
          id="main"
          className="mx-auto max-w-[88rem] px-4 pb-[calc(8rem+env(safe-area-inset-bottom))] pt-4 sm:px-6 lg:px-10 lg:pb-14 lg:pt-7"
        >
          <div key={path.split('/')[1]} className="animate-rise">
            <Outlet />
          </div>
        </main>

        <nav
          aria-label={t('nav.menu')}
          className="fixed inset-x-3 bottom-[max(0.75rem,env(safe-area-inset-bottom))] z-30 lg:hidden print:hidden"
        >
          <div className="mx-auto flex h-[4.25rem] max-w-lg items-center justify-around rounded-[26px] border border-white/80 bg-panel/90 px-1.5 shadow-pop backdrop-blur-xl backdrop-saturate-150 dark:border-line">
            {tabs.slice(0, 2).map((n) => (
              <TabLink key={n.to} to={n.to} active={isActive(n.to)} label={t(n.label)}>
                <n.icon className="size-[21px]" aria-hidden />
              </TabLink>
            ))}
            {showQuick && (
              <button
                onClick={() => setQuickOpen(true)}
                aria-label={t('nav.quick')}
                className="-mt-7 grid size-14 shrink-0 place-items-center rounded-[20px] bg-brand text-brand-ink shadow-[0_12px_22px_-8px_color-mix(in_oklab,var(--brand)_80%,transparent)] transition-transform active:scale-95"
              >
                <Plus className="size-6" strokeWidth={2.4} aria-hidden />
              </button>
            )}
            {tabs.slice(2).map((n) => (
              <TabLink key={n.to} to={n.to} active={isActive(n.to)} label={t(n.label)}>
                <n.icon className="size-[21px]" aria-hidden />
              </TabLink>
            ))}
            {overflow.length > 0 && (
              <button
                onClick={() => setMoreOpen(true)}
                className={cn(
                  'flex min-h-14 flex-1 flex-col items-center justify-center gap-1 text-[11px] font-medium text-muted',
                  overflow.some((n) => isActive(n.to)) && 'font-semibold text-brand',
                )}
              >
                <TabIcon active={overflow.some((n) => isActive(n.to))}>
                  <LayoutGrid className="size-[21px]" aria-hidden />
                </TabIcon>
                {t('nav.more')}
              </button>
            )}
          </div>
        </nav>

        <Dialog open={moreOpen} onOpenChange={setMoreOpen} title={t('nav.menu')}>
          <div className="flex flex-col gap-4">
            <ProfileCard />
            {overflow.length > 0 && (
              <div className="grid grid-cols-2 gap-2.5">
                {overflow.map((n) => (
                  <Tile
                    key={n.to}
                    icon={n.icon}
                    label={t(n.label)}
                    tone="bg-brand-soft text-brand-2"
                    active={isActive(n.to)}
                    to={n.to}
                    onClick={() => setMoreOpen(false)}
                  />
                ))}
              </div>
            )}
            <AccountControls />
          </div>
        </Dialog>

        <Dialog open={quickOpen} onOpenChange={setQuickOpen} title={t('nav.quick_title')}>
          <div className="grid grid-cols-2 gap-2.5">
            {quick.map((q) => (
              <Tile
                key={q.label}
                icon={q.icon}
                label={t(q.label)}
                tone={q.tone}
                onClick={() => runQuick(q)}
              />
            ))}
          </div>
        </Dialog>

        {paletteOpen && (
          <Suspense fallback={null}>
            <CommandPalette open onOpenChange={setPaletteOpen} />
          </Suspense>
        )}
      </div>
    </TitleSlot.Provider>
  );
}

function RailGroup({ children }: { children: ReactNode }) {
  return (
    <div className="flex flex-col items-center gap-2 rounded-full border border-white/80 bg-panel/75 p-2.5 shadow-card backdrop-blur dark:border-line">
      {children}
    </div>
  );
}

function RailLink({ item, label, active }: { item: NavItem; label: string; active: boolean }) {
  return (
    <Link
      to={item.to}
      aria-label={label}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'group relative grid size-11 place-items-center rounded-full transition-[background-color,color,box-shadow] duration-200',
        active
          ? 'bg-brand text-brand-ink shadow-[0_8px_16px_-6px_color-mix(in_oklab,var(--brand)_75%,transparent)]'
          : 'bg-panel text-rail-ink shadow-[0_1px_2px_rgb(40_44_110/0.06)] hover:bg-soft',
      )}
    >
      <item.icon className="size-5" aria-hidden />
      <span
        aria-hidden
        className="pointer-events-none absolute start-[calc(100%+0.75rem)] top-1/2 z-10 -translate-y-1/2 whitespace-nowrap rounded-lg bg-ink px-2.5 py-1.5 text-xs font-medium text-panel opacity-0 shadow-pop transition-opacity duration-150 group-hover:opacity-100 group-focus-visible:opacity-100"
      >
        {label}
      </span>
    </Link>
  );
}

function TabLink({
  to,
  active,
  label,
  children,
}: {
  to: string;
  active: boolean;
  label: string;
  children: ReactNode;
}) {
  return (
    <Link
      to={to}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'flex min-h-14 flex-1 flex-col items-center justify-center gap-1 text-[11px] font-medium text-muted transition-colors',
        active && 'font-semibold text-brand',
      )}
    >
      <TabIcon active={active}>{children}</TabIcon>
      {label}
    </Link>
  );
}

function TabIcon({ active, children }: { active: boolean; children: ReactNode }) {
  return (
    <span
      className={cn(
        'grid h-8 w-12 place-items-center rounded-full transition-[background-color,color] duration-200',
        active && 'bg-brand-soft text-brand',
      )}
    >
      {children}
    </span>
  );
}

function Tile({
  icon: Icon,
  label,
  tone,
  active,
  to,
  onClick,
}: {
  icon: ComponentType<{ className?: string }>;
  label: string;
  tone: string;
  active?: boolean;
  to?: string;
  onClick: () => void;
}) {
  const body = (
    <>
      <span className={cn('grid size-10 place-items-center rounded-[14px]', tone)}>
        <Icon className="size-5" aria-hidden />
      </span>
      <span className="font-medium">{label}</span>
    </>
  );
  const cls = cn(
    'flex min-h-24 flex-col justify-between gap-3 rounded-[22px] bg-tile p-3.5 text-start transition-transform active:scale-[0.97]',
    active && 'ring-2 ring-brand/40',
  );
  return to ? (
    <Link to={to} onClick={onClick} aria-current={active ? 'page' : undefined} className={cls}>
      {body}
    </Link>
  ) : (
    <button type="button" onClick={onClick} className={cls}>
      {body}
    </button>
  );
}

function QuickMenu({ items, onPick }: { items: QuickItem[]; onPick: (q: QuickItem) => void }) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <Button size="icon" className="hidden size-12 lg:grid" aria-label={t('nav.quick')}>
          <Plus className="!size-5" strokeWidth={2.4} />
        </Button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align={i18n.dir() === 'rtl' ? 'start' : 'end'}
          sideOffset={10}
          collisionPadding={16}
          className="z-50 w-72 animate-pop rounded-[22px] bg-panel p-2 shadow-pop ring-1 ring-line/60 focus:outline-none"
        >
          <div className="px-3 pb-1.5 pt-2 text-xs font-medium text-muted">{t('nav.quick')}</div>
          {items.map((q) => (
            <button
              key={q.label}
              type="button"
              onClick={() => {
                setOpen(false);
                onPick(q);
              }}
              className="flex min-h-12 w-full items-center gap-3 rounded-2xl px-2.5 text-start text-[15px] transition-colors hover:bg-soft focus-visible:bg-soft"
            >
              <span className={cn('grid size-9 place-items-center rounded-xl', q.tone)}>
                <q.icon className="size-[18px]" aria-hidden />
              </span>
              {t(q.label)}
            </button>
          ))}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

function UserMenu() {
  const { t, i18n } = useTranslation();
  const { profile } = useAuth();
  const farm = useFarmSettings().data;
  return (
    <Popover.Root>
      <Popover.Trigger asChild>
        <button
          aria-label={profile?.full_name ?? t('nav.menu')}
          className="hidden h-12 items-center gap-2.5 rounded-full border border-white/80 bg-panel/80 pe-3 ps-1.5 shadow-card transition-colors hover:bg-panel lg:flex dark:border-line"
        >
          <img
            src="/logo.png"
            alt=""
            className="size-9 rounded-full bg-white object-contain p-0.5 ring-1 ring-line"
          />
          <span className="max-w-40 text-start leading-tight">
            <span className="block truncate text-[13.5px] font-semibold">
              {farm?.farm_name ?? t('app.farm')}
            </span>
            <span className="block truncate text-xs text-muted">{profile?.full_name}</span>
          </span>
          <ChevronsUpDown className="size-4 text-muted" aria-hidden />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align={i18n.dir() === 'rtl' ? 'start' : 'end'}
          sideOffset={10}
          collisionPadding={16}
          className="z-50 flex w-80 animate-pop flex-col gap-3 rounded-[24px] bg-panel p-3 shadow-pop ring-1 ring-line/60 focus:outline-none"
        >
          <ProfileCard />
          <AccountControls />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

function ProfileCard() {
  const { t } = useTranslation();
  const { profile } = useAuth();
  return (
    <div className="flex items-center gap-3 rounded-[18px] bg-tile p-3">
      <Avatar name={profile?.full_name} />
      <div className="min-w-0">
        <div className="truncate font-medium">{profile?.full_name}</div>
        {profile && (
          <div className="truncate text-xs text-muted">{t(`settings.roles.${profile.role}`)}</div>
        )}
      </div>
    </div>
  );
}

function AccountControls() {
  const { t } = useTranslation();
  const { signOut } = useAuth();
  return (
    <>
      <ThemeToggle />
      <LanguageSwitcher />
      <Button variant="secondary" onClick={() => void signOut()}>
        <LogOut aria-hidden className="rtl:-scale-x-100" /> {t('nav.logout')}
      </Button>
    </>
  );
}

function ThemeToggle() {
  const { t } = useTranslation();
  const theme = useTheme();
  const opts: { value: ThemePref; label: string; icon: typeof Sun }[] = [
    { value: 'light', label: t('nav.theme_light'), icon: Sun },
    { value: 'dark', label: t('nav.theme_dark'), icon: Moon },
    { value: 'system', label: t('nav.theme_system'), icon: Monitor },
  ];
  return (
    <Segmented
      label={t('common.theme')}
      className="w-full [&>button]:flex-1"
      value={theme}
      onChange={setTheme}
      options={opts}
    />
  );
}

function Avatar({ name }: { name: string | undefined }) {
  const initials = (name ?? '')
    .split(/\s+/)
    .filter((w) => /^\p{L}/u.test(w))
    .slice(0, 2)
    .map((w) => w[0])
    .join('');
  return (
    <span
      className="grid size-10 shrink-0 place-items-center rounded-full bg-gradient-to-br from-[#8b8ff0] to-brand text-[13px] font-semibold text-white shadow-[0_4px_10px_-4px_color-mix(in_oklab,var(--brand)_70%,transparent)]"
      aria-hidden
    >
      {initials || '·'}
    </span>
  );
}

export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle?: ReactNode;
  actions?: ReactNode;
}) {
  const slot = useContext(TitleSlot);
  const heading = (
    <div className="min-w-0">
      <h1 className="truncate text-[21px] font-semibold leading-tight tracking-tight lg:text-[28px]">
        {title}
      </h1>
      {subtitle && (
        <p className="mt-0.5 truncate text-[13px] text-muted lg:mt-1 lg:text-sm">{subtitle}</p>
      )}
    </div>
  );
  return (
    <>
      {slot && createPortal(heading, slot)}
      {actions && <div className="flex max-w-full flex-wrap items-center gap-2">{actions}</div>}
    </>
  );
}
